import type { AppConfig } from '@/config/env';
import { loadIntercomWidget } from './intercomLoader';
import { readHandoffDeviceId } from './deviceId';
import {
  createExchangeRate,
  createRevenueResolver,
  googleEventNames,
  pinterestEventNames,
  purchaseConfig,
  snapchatEventNames,
  telemetryEventNames,
  telemetryRevenueEvents,
  tiktokEventNames,
} from './pixels';
import type {
  AnalyticsLogger,
  AnalyticsUser,
  IAnalyticsProvider,
  SendConversionRequestWithMeta,
  SendEventRequestWithMeta,
  UpdatePropertiesRequestWithMeta,
} from '@organicapps/web-analytics';

class DevAnalyticsProvider implements IAnalyticsProvider {
  constructor(private readonly sink: (event: string, payload: unknown) => void) {}

  async init() {
    this.sink('init', {});
  }

  async sendUserEvent(payload: SendEventRequestWithMeta) {
    this.sink('user', payload);
  }

  async sendUpdateProperties(payload: UpdatePropertiesRequestWithMeta) {
    this.sink('updateProperties', payload);
  }

  async sendConversion(payload: SendConversionRequestWithMeta) {
    this.sink('conversion', payload);
  }
}

/** Cookie value by name; avoids a static import of web-analytics. */
/**
 * Same key as WebQuiz (`KVFactory('analytics')` → `[[analytics]]-isFirstAction`) and the same storage:
 * WebQuiz's default KV engine is sessionStorage, so the first action is once per tab, like `Install`.
 * The funnel used localStorage here, which made it once per browser for good — a returning visitor,
 * or anyone testing in a new tab, never sent `WebFirstAction` again.
 */
const FIRST_ACTION_KEY = '[[analytics]]-isFirstAction';

const readFirstActionFlag = (): string | null => {
  try {
    // The localStorage copy is the old, never-expiring flag; dropping it keeps the two from disagreeing.
    globalThis.localStorage?.removeItem(FIRST_ACTION_KEY);
    return globalThis.sessionStorage?.getItem(FIRST_ACTION_KEY) ?? null;
  } catch {
    return null;
  }
};

const writeFirstActionFlag = (value: string): void => {
  try {
    globalThis.sessionStorage?.setItem(FIRST_ACTION_KEY, value);
  } catch {
    // Storage can be blocked (private mode, disabled site data); the in-memory flag still holds for this run.
  }
};

export const getCookieValue = (name: string): string => {
  try {
    const prefix = `${name}=`;
    for (const part of document.cookie.split(';')) {
      const trimmed = part.trim();
      if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.slice(prefix.length));
    }
  } catch {
    // no document (tests) or blocked cookies
  }
  return '';
};

const loggerAdapter = (logger: { logInfo: (data: unknown, message?: string) => void }): AnalyticsLogger => ({
  logInfo: logger.logInfo,
});

export interface FunnelAnalyticsOptions {
  /**
   * Resolves once the anonymous sign-in has filled in `appUserId` / `accessToken`.
   *
   * Providers read the user at construction time (the backend provider is only added when a token
   * exists) and again inside `init()` (`user.analyticsSettings[provider].pixelId` overrides the env
   * key, and Amplitude/PostHog identify by `appUserId`). Building them from an empty user is what
   * produced anonymous profiles, a missing backend provider and ignored per-user pixel ids — so the
   * whole core waits here, exactly as WebQuiz waits for `user.appUserId && user.accessToken`.
   *
   * The caller settles this even when sign-in fails: an anonymous send beats losing every event.
   */
  waitForUser?: Promise<unknown>;
}

export const createFunnelAnalytics = (
  config: AppConfig,
  logger: { logInfo: (data: unknown, message?: string) => void },
  user: AnalyticsUser = { appUserId: 'anonymous', accessToken: '' },
  options: FunnelAnalyticsOptions = {},
) => {
  type AnalyticsCore = ReturnType<(typeof import('@organicapps/web-analytics'))['createAnalytics']>;
  let corePromise: Promise<AnalyticsCore> | undefined;

  const createCore = async (): Promise<AnalyticsCore> => {
    if (options.waitForUser) {
      await options.waitForUser.catch(() => undefined);
      logger.logInfo({ appUserId: user.appUserId, identified: Boolean(user.accessToken) }, 'analyticsUserReady');
    }
    const { createAnalytics } = await import('@organicapps/web-analytics');
    const provider = new DevAnalyticsProvider((event, payload) => {
      logger.logInfo({ event, payload }, 'analyticsEvent');
    });
    const providers: IAnalyticsProvider[] = [provider];

    const addSafeProvider = (name: string, candidate: IAnalyticsProvider) => {
      providers.push({
        async init() {
          try {
            await candidate.init();
          } catch (error) {
            logger.logInfo({ provider: name, error: String(error) }, 'analyticsProviderInitFailed');
          }
        },
        async sendUserEvent(payload) {
          try {
            await candidate.sendUserEvent(payload);
          } catch (error) {
            logger.logInfo({ provider: name, error: String(error) }, 'analyticsProviderEventFailed');
          }
        },
        async sendUpdateProperties(payload) {
          try {
            await candidate.sendUpdateProperties(payload);
          } catch (error) {
            logger.logInfo({ provider: name, error: String(error) }, 'analyticsProviderPropertiesFailed');
          }
        },
        async sendConversion(payload) {
          try {
            await candidate.sendConversion(payload);
          } catch (error) {
            logger.logInfo({ provider: name, error: String(error) }, 'analyticsProviderConversionFailed');
          }
        },
      });
    };

    // Amplitude is the main product tracking; runs in dev too, only replay sampling differs.
    let amplitudeProvider: import('@organicapps/web-analytics/providers/amplitude').default | undefined;
    if (config.amplitudeApiKey) {
      const { default: AmplitudeProvider } = await import('@organicapps/web-analytics/providers/amplitude');
      // A device id handed over by the landing keeps one visit as one device in Amplitude.
      const handoffDeviceId = readHandoffDeviceId(config.search);
      amplitudeProvider = new AmplitudeProvider({
        user,
        apiKey: config.amplitudeApiKey,
        sessionReplaySampleRate: config.analyticsMode === 'production' ? 0.02 : 1,
        ...(handoffDeviceId ? { deviceId: handoffDeviceId } : {}),
      });
      addSafeProvider('amplitude', amplitudeProvider);
      if (config.vwoEnabled) {
        const [{ default: VwoProvider }, { default: VWOAmplitudePlugin }] = await Promise.all([
          import('@organicapps/web-analytics/providers/vwo'),
          import('vwo-amplitude-integration'),
        ]);
        addSafeProvider('vwo', new VwoProvider(amplitudeProvider, (amp) => VWOAmplitudePlugin(amp)));
      }
    }
    // PostHog needs key AND host: no host → US cloud, no key → its init hangs the analytics Promise.all.
    if (config.posthog) {
      const { default: PosthogProvider } = await import('@organicapps/web-analytics/providers/posthog');
      addSafeProvider(
        'posthog',
        new PosthogProvider({
          user,
          apiKey: config.posthog.apiKey,
          apiHost: config.posthog.apiHost,
          // Heatmaps group clicks by URL: every quiz screen has its own (see funnel/screenRoute).
          options: { enable_heatmaps: true },
        }),
      );
    }
    if (config.intercom) {
      // The provider only boots the widget; without the snippet `window.Intercom` does not exist and
      // the boot is silently dropped.
      loadIntercomWidget(config.intercom.appId);
      const { default: IntercomProvider } = await import('@organicapps/web-analytics/providers/intercom');
      addSafeProvider(
        'intercom',
        new IntercomProvider({
          user,
          appId: config.intercom.appId,
          apiBase: config.intercom.apiBase,
          productName: config.productName,
        }),
      );
    }

    // Shared by the ad pixels (net of fees) and Jitsu (gross): one cache, one endpoint.
    const exchangeRate = createExchangeRate(config, logger);

    if (config.analyticsMode === 'production') {
      // Without an eventNameMapper the pixels only ever see the meta events (Install, First Action):
      // the paywall view and the purchase never reached them. Values are net of processor fees, as
      // in WebPaywalls, so the ad platforms optimise on the same number in both apps.
      const purchase = purchaseConfig(exchangeRate);
      // Real email when the funnel already has one (registration / SSO); providers fall back to the
      // synthetic `${appUserId}@myorganicapps.com` otherwise, exactly as WebPaywalls does.
      const resolveEmail = () => (user as { email?: string }).email;
      if (config.snapchatPixelId) {
        const { default: SnapchatProvider } = await import('@organicapps/web-analytics/providers/snapchat');
        addSafeProvider(
          'snapchat',
          new SnapchatProvider({
            user,
            pixelId: config.snapchatPixelId,
            productName: config.productName,
            includeItemCategory: true,
            resolveEmail,
            eventNameMapper: snapchatEventNames,
            purchase,
          }),
        );
      }
      // Meta stays init-only, as in WebPaywalls (META_BROWSER_PIXEL_EVENTS_DISABLED): the events come
      // from the backend via CAPI, the browser pixel is here to keep `_fbp` available for matching.
      if (config.facebookPixelId) {
        const { default: FacebookProvider } = await import('@organicapps/web-analytics/providers/facebook');
        addSafeProvider('facebook', new FacebookProvider({ user, pixelId: config.facebookPixelId, gender: 'f' }));
      }
      if (config.googleTagId) {
        const { default: GoogleProvider } = await import('@organicapps/web-analytics/providers/google');
        addSafeProvider(
          'google',
          new GoogleProvider({ user, tagId: config.googleTagId, eventNameMapper: googleEventNames, purchase }),
        );
      }
      if (config.tiktokPixelId) {
        const { default: TiktokProvider } = await import('@organicapps/web-analytics/providers/tiktok');
        addSafeProvider(
          'tiktok',
          new TiktokProvider({
            user,
            pixelId: config.tiktokPixelId,
            productName: config.productName,
            includeEventId: true,
            resolveEmail,
            eventNameMapper: tiktokEventNames,
            purchase,
          }),
        );
      }
      if (config.pinterestTagId) {
        const { default: PinterestProvider } = await import('@organicapps/web-analytics/providers/pinterest');
        addSafeProvider(
          'pinterest',
          new PinterestProvider({
            user,
            tagId: config.pinterestTagId,
            resolveEmail,
            eventNameMapper: pinterestEventNames,
            purchase,
          }),
        );
      }
    }

    if (config.analyticsApiUrl && user.accessToken) {
      const { default: BackendProvider } = await import('@organicapps/web-analytics/providers/backend');
      // Every gateway client here sends the landing; `/analytics/conversion` needs it to find the landing's
      // pixel config, and without it the backend 500s on `getPixelId` of undefined.
      const backendHeaders = (accessToken: string) => ({
        'content-type': 'application/json',
        'x-session-token': accessToken,
        'x-app-type': config.productName,
        'x-landing': config.productName,
      });
      providers.push(
        new BackendProvider({
          user,
          transport: {
            sendUserEvent: (accessToken, payload) =>
              fetch(`${config.analyticsApiUrl}/analytics/user-event`, {
                method: 'POST',
                headers: backendHeaders(accessToken),
                body: JSON.stringify(payload),
              }),
            sendUpdateProperties: (accessToken, payload) =>
              fetch(`${config.analyticsApiUrl}/v1/user/update-properties`, {
                method: 'POST',
                headers: backendHeaders(accessToken),
                body: JSON.stringify(payload),
              }),
            sendConversion: (accessToken, payload) =>
              fetch(`${config.analyticsApiUrl}/analytics/conversion`, {
                method: 'POST',
                headers: backendHeaders(accessToken),
                body: JSON.stringify(payload),
              }),
          },
        }),
      );
    }

    // Not gated by analyticsMode (WebPaywalls parity): the key+host decide, so DEV can verify the events.
    if (config.analyticsWriteKey && config.analyticsHost) {
      const { default: TelemetryProvider } = await import('@organicapps/web-analytics/providers/telemetry');
      providers.push(
        new TelemetryProvider({
          user,
          productName: config.productName,
          writeKey: config.analyticsWriteKey,
          host: config.analyticsHost,
          // Identify by id alone: the synthetic `${appUserId}@myorganicapps.com` the provider builds
          // by default is not an address anyone can match on, and it pollutes the Jitsu profile.
          identifyEmail: false,
          // The provider attaches this to every event (a per-user override from the backend wins);
          // without it each event carried `fbPixelId: undefined`.
          fbPixelId: config.facebookPixelId,
          eventNameMapper: telemetryEventNames,
          revenueEvents: telemetryRevenueEvents,
          resolveRevenue: createRevenueResolver(exchangeRate),
          // Objects pass through as they are (WebPaywalls parity); the default JSON-strings them.
          stringifyNestedProperties: false,
          // Jitsu reads geo from `context`, not from properties; the sign-in response carries it.
          payloadContext: () => {
            const { city, country } = user as { city?: string; country?: string };
            return { context: { geo: { city: { name: city }, country: { code: country } } } };
          },
        }),
      );
    }

    return createAnalytics({ providers, logger: loggerAdapter(logger) });
  };

  const getCore = () => {
    corePromise ??= createCore();
    return corePromise;
  };

  let isFirstAction = readFirstActionFlag() !== 'false';

  /**
   * Flushing before the providers are up loses events: posthog-js drops a `capture()` made before
   * `init()` ("You must initialize PostHog before calling capture"), while Amplitude buffers it — which
   * is exactly why an early event used to show up in one and not the other. So a flush waits for the
   * init that is already running; with no init in flight it goes out as before.
   */
  let initInFlight: Promise<unknown> | undefined;

  return {
    async init(properties: Record<string, unknown>) {
      initInFlight = getCore().then((instance) => instance.init(properties));
      await initInFlight;
    },
    /**
     * Providers only, no `Install`: after a reload in the same tab (provider redirect) `Install` is
     * already sent, but posthog/amplitude/pixels of this page load still need their own init.
     */
    async initProviders() {
      initInFlight ??= getCore().then(async (core) => {
        if (core.isInit) return;
        await Promise.all(core.analyticsProviders.map((provider) => provider.init()));
        core.isInit = true;
      });
      await initInFlight;
    },
    // The core promise is the queue: `.then` callbacks run in registration order, keeping event order.
    pushUserEvent(payload: SendEventRequestWithMeta) {
      void getCore().then((instance) => instance.pushUserEvent(payload));
    },
    pushUpdateProperties(payload: UpdatePropertiesRequestWithMeta) {
      void getCore().then((instance) => instance.pushUpdateProperties(payload));
    },
    /**
     * Pixel-facing conversions (WebPaywall, WebPurchase) — the queue the ad platforms read, and what
     * the backend builds Meta CAPI from. Build the payload with `buildConversion` so price and the
     * matching parameters travel with it.
     */
    pushConversion(payload: { event: string; [key: string]: unknown }) {
      void getCore().then((instance) => instance.pushConversion(payload as never));
    },
    async sendEvents() {
      // Nothing to wait for means the flush races the providers again — worth a line in the log,
      // because the loss is silent otherwise.
      if (!initInFlight) logger.logInfo({}, 'analyticsSendBeforeInit');
      // An init failure must not hold the queue: providers that are up still get the event.
      await initInFlight?.catch(() => undefined);
      await (await getCore()).sendEvents();
    },
    get isFirstAction() {
      return isFirstAction;
    },
    /**
     * `First Action` / `WebFirstAction` fire once per tab session, paired with `Install`. The flag is
     * persisted only after the core has the events: the core is built lazily behind the sign-in, and
     * persisting first meant a failure there lost the first action for that session. The in-memory flag
     * drops right away so a burst of clicks cannot queue it twice.
     */
    async setIsFirstAction() {
      if (!isFirstAction) return;
      isFirstAction = false;
      try {
        const core = await getCore();
        await core.setIsFirstAction();
        writeFirstActionFlag('false');
      } catch (error) {
        isFirstAction = true;
        logger.logInfo({ error: String(error) }, 'analyticsFirstActionFailed');
      }
    },
  };
};
